<template>
	<div class="bulk-actions-section">
		<!-- Jobs List -->
		<div v-if="jobs && jobs.length > 0" class="jobs-history">
			<div class="jobs-list-header">
				<span>{{ $t('text.jobHistory') }}</span>
			</div>
			
			<div class="jobs-list">
				<div 
					v-for="job in sortedJobs" 
					:key="job.jobId"
					class="job-item"
					:class="{ 'running': isJobRunning(job), 'failed': job.status === 'ERROR', 'completed': job.status === 'DONE' }"
					@click="$emit('view-job-details', job)"
				>
					<div class="job-item-content">
						<div class="job-item-status">
							<v-icon 
								:color="getStatusColor(job.status)" 
								size="16"
							>
								{{ getStatusIcon(job.status) }}
							</v-icon>
						</div>
						<div class="job-item-info">
							<div class="job-item-date">{{ formatJobDate(job.createdAt) }}</div>
							<div class="job-item-stats" v-if="job.statusData?.summary">
								<span class="stat success">{{ job.statusData.summary.successfulCancellations || 0 }}</span>
								<span class="stat-separator">/</span>
								<span class="stat error">{{ job.statusData.summary.failedCancellations || 0 }}</span>
							</div>
						</div>
						<div class="job-item-arrow">
							<v-icon size="16" color="#999">mdi-chevron-right</v-icon>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- Cancel All Button (less prominent) -->
		<v-btn
			class="defaultButton gradientButton cancel-all-btn"
			theme="dark"
			elevation="0"
			size="small"
			@click="$emit('cancel-orders')"
			:disabled="!canCancelOrders"
		>
			<v-icon size="16">mdi-cancel</v-icon>
			{{ $t('text.cancelAllOrders') }}
		</v-btn>
	</div>
</template>

<script>
export default {
	name: 'BulkActionsPanel',
	props: {
		selectedDay: {
			type: Object,
			default: null
		},
		selectedSlot: {
			type: Object,
			default: null
		},
		jobs: {
			type: Array,
			default: () => []
		}
	},
	emits: ['cancel-orders', 'view-job-details'],
	computed: {
		canCancelOrders() {
			return this.selectedDay && this.selectedSlot && (!this.jobs || this.jobs.length === 0)
		},
		sortedJobs() {
			if (!this.jobs) return []
			// Sort jobs by createdAt descending (latest first)
			return [...this.jobs].sort((a, b) => {
				return new Date(b.createdAt) - new Date(a.createdAt)
			})
		}
	},
	methods: {
		isJobRunning(job) {
			return job.status === 'NEW' || job.status === 'RUNNING'
		},
		getStatusColor(status) {
			switch (status) {
				case 'NEW': return '#2196f3'
				case 'RUNNING': return '#ff9800'
				case 'DONE': return '#4caf50'
				case 'ERROR': return '#f44336'
				default: return '#9e9e9e'
			}
		},
		getStatusIcon(status) {
			switch (status) {
				case 'NEW': return 'mdi-clock-outline'
				case 'RUNNING': return 'mdi-loading mdi-spin'
				case 'DONE': return 'mdi-check-circle'
				case 'ERROR': return 'mdi-alert-circle'
				default: return 'mdi-help-circle'
			}
		},
		formatJobDate(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleString('de-DE', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: false
			})
		}
	}
}
</script>

<style scoped>
.bulk-actions-section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.jobs-history {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.jobs-list-header {
	font-size: 11px;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	padding: 0 8px;
}

.jobs-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
	max-height: 200px;
	overflow-y: auto;
}

.job-item {
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.job-item:hover {
	border-color: #999;
	background: #f5f5f5;
}

.job-item.running {
	border-left: 3px solid #ff9800;
	background: #fff8e1;
}

.job-item.completed {
	border-left: 3px solid #4caf50;
}

.job-item.failed {
	border-left: 3px solid #f44336;
}

.job-item-content {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px;
}

.job-item-status {
	display: flex;
	align-items: center;
}

.job-item-info {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.job-item-date {
	font-size: 12px;
	color: #333;
	font-weight: 500;
}

.job-item-stats {
	font-size: 11px;
	color: #666;
	display: flex;
	align-items: center;
	gap: 4px;
}

.stat {
	font-weight: 600;
}

.stat.success {
	color: #4caf50;
}

.stat.error {
	color: #f44336;
}

.stat-separator {
	color: #ccc;
}

.job-item-arrow {
	display: flex;
	align-items: center;
}

.cancel-all-btn {
	margin-top: 12px;
	width: 100%;
}

@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}

.mdi-spin {
	animation: spin 1s linear infinite;
}
</style>
